import { useContext } from 'react';
import { SiteConfigContext } from '../SiteConfigProvider';

var removeSpecialCharacters = function removeSpecialCharacters(content) {
  return content.replace(/(<([^>]+)>)/gi, '').replace(/"/g, "'").replace(/&([a-z0-9]+|#[0-9]{1,6}|#x[0-9a-f]{1,6});/g, '');
};
/**
 * Function to generate Article ld+json schema
 *
 * @param {Object} props
 * @param {Object} props.author
 * @param {String} props.author.name
 * @param {String} props.canonical
 * @param {String} props.content
 * @param {String} props.published
 * @param {String} props.lastModified
 * @param {String} props.title
 * @param {Object} props.thumbnail
 *
 * @returns {import('schema-dts').Article}
 */


export function ArticleSchema(_ref) {
  var author = _ref.author,
      canonical = _ref.canonical,
      content = _ref.content,
      lastModified = _ref.lastModified,
      published = _ref.published,
      title = _ref.title,
      thumbnail = _ref.thumbnail; // ensure there are no html tag elements within the articleBody and author description
  // @todo: move this to utils, think this will be used in other schemas or areas

  var finalTextContent = removeSpecialCharacters(content);
  var textAuthor = author.bio ? author.bio.replace(/(<([^>]+)>)/gi, '') : '';

  var _useContext = useContext(SiteConfigContext),
      _useContext$siteConst = _useContext.siteConstants.ARTICLE_SCHEMA_PROPS,
      name = _useContext$siteConst.name,
      logoHeight = _useContext$siteConst.logoHeight,
      logoWidth = _useContext$siteConst.logoWidth,
      logoUrl = _useContext$siteConst.logoUrl;

  return {
    '@context': 'http://schema.org',
    '@type': 'Article',
    articleBody: finalTextContent,
    author: {
      '@type': 'Person',
      name: author.name,
      description: textAuthor,
      image: author.thumbnail
    },
    datePublished: published,
    dateModified: lastModified,
    inLanguage: 'en-us',
    headline: title,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical
    },
    publisher: {
      '@type': 'Organization',
      name: name,
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
        width: logoWidth,
        height: logoHeight
      }
    },
    image: {
      '@type': 'ImageObject',
      url: thumbnail.path
    }
  };
}
/**
 * Function to generate Breadcrumb schema ld+json schema
 *
 * @param {Object} props
 * @param {Object} props.primary
 * @param {String} props.primary.name
 * @param {String} props.primary.slug
 * @param {Object} props.sub
 * @param {String} props.sub.name
 * @param {String} props.sub.slug
 *
 * @returns {import('schema-dts').BreadcrumbList}
 */

export function BreadcrumbSchema(_ref2) {
  var _ref2$primary = _ref2.primary,
      primary = _ref2$primary === void 0 ? null : _ref2$primary,
      _ref2$sub = _ref2.sub,
      sub = _ref2$sub === void 0 ? null : _ref2$sub;

  var _useContext2 = useContext(SiteConfigContext),
      SITE_HOST = _useContext2.siteConstants.SITE_HOST;

  var primaryData = primary && {
    '@type': 'ListItem',
    position: 2,
    item: {
      '@id': "".concat(SITE_HOST, "/").concat(primary.slug),
      name: primary.name
    }
  };
  var subData = sub && {
    '@type': 'ListItem',
    position: 3,
    item: {
      // todo: update to use SITE_HOST from context and wrap story with MockSiteConfigProvider
      '@id': "".concat(SITE_HOST, "/").concat(sub.slug),
      name: sub.name
    }
  };
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      item: {
        '@id': "".concat(SITE_HOST),
        name: 'Home'
      }
    }, primary && primary.slug ? primaryData : '', sub && sub.slug ? subData : '']
  };
}
/**
 *
 * @param {String} props.canonical :: current url/canonical of the page
 * @param {Array} props.items :: list of different services or items
 * @param {String} props.title :: name of the page
 */

export function ItemListSchema(_ref3) {
  var authorName = _ref3.authorName,
      canonical = _ref3.canonical,
      items = _ref3.items,
      _ref3$items$isFeature = _ref3.items.isFeaturedBestPick,
      isFeaturedBestPick = _ref3$items$isFeature === void 0 ? false : _ref3$items$isFeature,
      modifiedDate = _ref3.modifiedDate,
      publishedDate = _ref3.publishedDate,
      title = _ref3.title;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListOrder: 'https://schema.org/ItemListOrderDescending',
    name: title,
    numberOfItems: items.length,
    url: canonical,
    itemListElement: items.map(function (item, i) {
      var bestPickDescription = item.bestPickDescription,
          fullTitle = item.fullTitle,
          _item$review = item.review,
          score = _item$review.score,
          reviewContent = _item$review.reviewContent,
          serviceName = item.serviceName,
          thumbnail = item.thumbnail;
      var reviewRating;

      if (score > 0) {
        reviewRating = {
          '@type': 'Rating',
          ratingValue: score,
          bestRating: 10,
          worstRating: 0
        };
      }

      return !isFeaturedBestPick && {
        '@type': 'ListItem',
        position: i + 1,
        name: fullTitle,
        item: {
          '@type': 'Product',
          name: serviceName,
          url: "".concat(canonical, "#").concat(serviceName.replace(/\s/g, '-')),
          description: bestPickDescription,
          image: {
            '@type': 'ImageObject',
            url: thumbnail
          },
          review: {
            '@type': 'Review',
            datePublished: publishedDate,
            dateModified: modifiedDate,
            reviewBody: removeSpecialCharacters(reviewContent || ''),
            reviewRating: reviewRating,
            author: {
              '@type': 'Person',
              name: authorName
            }
          },
          brand: {
            '@type': 'Thing',
            name: serviceName
          }
        }
      };
    })
  };
}
/**
 * Function to generate Organization Schema
 *
 * @param {String} canonical website canonical url
 * @param {String} logoUrl ogImage url defined in the meta data
 * @param {Array} sameAsUrls array of sameAs website urls
 *
 * @returns {import('schema-dts').Organization}
 */

export function OrganizationSchema(canonical, logoUrl, sameAsUrls) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: canonical,
    logo: logoUrl,
    sameAs: sameAsUrls && sameAsUrls.map(function (url) {
      return url;
    })
  };
}
/**
 * Function to generate Person ld+json schema
 *
 * @param {Object} props
 * @param {Array} props.educations
 * @param {Object} props.users
 *
 * @returns {import('schema-dts').Person}
 */

export function PersonSchema(_ref4) {
  var educations = _ref4.educations,
      _ref4$users = _ref4.users,
      about_me = _ref4$users.about_me,
      business = _ref4$users.business,
      _ref4$users$business = _ref4$users.business,
      job_title = _ref4$users$business.job_title,
      website = _ref4$users$business.website,
      _ref4$users$contact = _ref4$users.contact,
      zip = _ref4$users$contact.zip,
      city = _ref4$users$contact.city,
      phone = _ref4$users$contact.phone,
      street = _ref4$users$contact.street,
      state = _ref4$users$contact.state,
      email = _ref4$users$contact.email,
      image = _ref4$users.image,
      name = _ref4$users.name,
      social = _ref4$users.social;
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    image: {
      '@type': 'ImageObject',
      url: "https://www.business.com".concat(image),
      caption: name
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressRegion: state,
      postalCode: zip,
      streetAddress: street
    },
    description: about_me,
    email: email,
    jobTitle: job_title,
    name: name,
    alumniOf: educations && educations.map(function (education) {
      var institution = education.institution;
      return {
        name: institution
      };
    }),
    telephone: phone,
    url: 'http://www.example.com',
    sameAs: social && Object.values(social).filter(function (socialLink) {
      return socialLink !== '';
    }).map(function (socialLink) {
      return socialLink;
    }),
    worksFor: {
      '@type': 'Organization',
      '@id': 'website.com/#organization',
      name: business.name,
      url: website
    }
  };
}
/**
 * Function to generate WebSite Schema
 *
 * @param {String} canonical WebSite canonical url
 *
 * @returns {import('schema-dts').WebSite}
 */

export function SearchSchema(canonical) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: "".concat(canonical),
    potentialAction: {
      '@type': 'SearchAction',
      target: "".concat(canonical, "/search/?q={search_term_string}"),
      'query-input': 'required name=search_term_string'
    }
  };
}